import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  IconButton, Collapse, Chip, Button, Pagination, MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { sendServerRequest } from '../../../server/request';
import { openGlobalSnack } from '../../utils/snack';
import { CircularLoading } from '../../utils/loading';

const ENDPOINT = '/server/cltCommand/sumaShotDebug';
const PAGE_SIZE = 20;

type SumaShotDebugLog = {
  id: string;
  cpf: string;
  provider: string | null;
  dispatched: boolean;
  reason: string;
  response_data: string | null;
  createdAt: string;
};

const REASON_LABELS: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'info' }> = {
  dispatched: { label: 'Despachado', color: 'success' },
  noCpf: { label: 'Sem CPF', color: 'error' },
  recentShot: { label: 'Shot Recente', color: 'warning' },
  recentChat: { label: 'Chat Recente', color: 'warning' },
  noConsultData: { label: 'Sem Dados da Consulta', color: 'error' },
  noMargin: { label: 'Sem Margem', color: 'error' },
  noForm: { label: 'Sem Form', color: 'error' },
  noClient: { label: 'Sem Cliente', color: 'error' },
  error: { label: 'Erro', color: 'error' },
};

function reasonChip(reason: string) {
  return REASON_LABELS[reason] ?? { label: reason, color: 'info' as const };
}

// Linhas antigas (antes da coluna provider ser preenchida) eram todas da Suma.
function providerLabel(provider: string | null) {
  return provider === 'v8' ? 'V8' : 'Suma';
}

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

function prettyJson(raw: string | null) {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function SumaShotDebugPanel() {
  const [logs, setLogs] = useState<SumaShotDebugLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [cpf, setCpf] = useState('');
  const [provider, setProvider] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function load(toPage: number) {
    setLoading(true);
    const q = new URLSearchParams({ page: String(toPage), pageSize: String(PAGE_SIZE) });
    if (cpf.trim()) q.set('cpf', cpf.trim());
    if (provider) q.set('provider', provider);
    sendServerRequest('GET', `${ENDPOINT}/list?${q.toString()}`)
      .then((e) => {
        const data = e.data?.data ?? {};
        setLogs(data.logs ?? []);
        setTotal(data.total ?? 0);
        setPage(toPage);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar os logs de disparo Suma.', 'error');
      });
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <h3 style={{ marginBottom: 4, textAlign: 'center' }}>Debug Disparo Shot CLT</h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0, marginBottom: 16, textAlign: 'center' }}>
        Histórico dos disparos de shot CLT dos providers (V8 e Suma), incluindo erros — últimos 7 dias.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <TextField size="small" label="CPF" value={cpf} onChange={(e) => setCpf(e.target.value)} />
        <TextField
          size="small"
          select
          label="Provider"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          style={{ minWidth: 120 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="v8">V8</MenuItem>
          <MenuItem value="suma">Suma</MenuItem>
        </TextField>
        <Button variant="contained" onClick={() => load(1)}>
          Filtrar
        </Button>
        <IconButton onClick={() => load(page)}>
          <RefreshIcon />
        </IconButton>
      </div>

      {loading ? (
        <CircularLoading />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Data</TableCell>
                <TableCell>CPF</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Motivo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ color: '#00000080' }}>
                    Nenhum log.
                  </TableCell>
                </TableRow>
              )}
              {logs.map((l) => (
                <React.Fragment key={l.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpanded(expanded === l.id ? null : l.id)}>
                        {expanded === l.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.createdAt)}</TableCell>
                    <TableCell>{l.cpf}</TableCell>
                    <TableCell>
                      <Chip size="small" label={providerLabel(l.provider)} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={l.dispatched ? 'Sucesso' : 'Falha'}
                        color={l.dispatched ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={reasonChip(l.reason).label}
                        color={reasonChip(l.reason).color}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} style={{ paddingTop: 0, paddingBottom: 0, border: 0 }}>
                      <Collapse in={expanded === l.id} unmountOnExit>
                        <div style={{ padding: 12, background: '#00000008' }}>
                          <div style={{ fontSize: 12, marginBottom: 8 }}>
                            <b>CPF:</b> {l.cpf} &nbsp;|&nbsp;
                            <b>Provider:</b> {providerLabel(l.provider)} &nbsp;|&nbsp;
                            <b>Despachado:</b> {l.dispatched ? 'Sim' : 'Não'} &nbsp;|&nbsp;
                            <b>Motivo:</b> {l.reason}
                          </div>
                          <b style={{ fontSize: 12 }}>Resposta Completa:</b>
                          <pre
                            style={{
                              fontSize: 11,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              maxHeight: 300,
                              overflow: 'auto',
                              margin: '4px 0 0',
                              background: '#0000000a',
                              padding: 8,
                              borderRadius: 4,
                            }}
                          >
                            {prettyJson(l.response_data) || '—'}
                          </pre>
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <Pagination count={pages} page={page} onChange={(_, p) => load(p)} />
        </div>
      )}
    </div>
  );
}
